import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {memo} from 'react'
import {useRouter} from 'next/router'
import Link from 'next/link'
import type {Block} from '@/services/kontent-ai/types'
import type {BazaarVoiceRatingsContentItem} from '@/products/ratings'
import {BazaarVoiceRatingsBlock} from '@/products/ratings'
import type {SkuProviderContentItem} from '@/products/sku-provider'
import {SkuProviderBlock} from '@/products/sku-provider'
import type {PearCommerceWidgetContentItem} from '@/products/where-to-buy/pearcommerce/pear-shoppable-widget'
import {PearCommerceWidget} from '@/products/where-to-buy/pearcommerce/pear-shoppable-widget'
import type {PDPHeaderAccordionContentItem} from '@/products/pdp/pdp-accordion/pdp-accordion'
import {PDPHeaderAccordionBlock} from '@/products/pdp/pdp-accordion/pdp-accordion'
import type {PDPPopupRebatesContentItem} from '@/products/pdp/pdp-popup-rebates/pdp-popup-rebates'
import {PDPPopupRebatesBlock} from '@/products/pdp/pdp-popup-rebates/pdp-popup-rebates'
import type {PDPPopupWithRichTextContentItem} from '@/products/pdp/pdp-popup-rich-text/pdp-popup-with-rich-text'
import {PDPPopupWithRichTextBlock} from '@/products/pdp/pdp-popup-rich-text/pdp-popup-with-rich-text'
import type {PDPSliderContentItem} from '@/products/flexible-web-toolkit/blocks/slider'
import {SliderBlock} from '@/products/flexible-web-toolkit/blocks/slider'
import {pushToDataLayer} from '@/utilities/analytics'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {TextButton} from '../../flexible-web-toolkit/components/button/text-button'
import {
	PDPFindAVetCard,
	type PDPFindAVetCardContentItem,
} from '../pdp-find-a-vet-card/pdp-find-a-vet-card'
import {getBrandColorClasses} from '../utils/brand-colors'
import {Button} from '../../flexible-web-toolkit/components/button/button'
import type {CtaButtonContentItem} from '../../flexible-web-toolkit/blocks/cta-button'
import {
	MikMakButtonBlock,
	type MikmakButtonContentItem,
} from '../../where-to-buy/mikmak/mikmak-button'

export type PDPHeaderBlockContentItem = IContentItem<{
	slider: Elements.LinkedItemsElement<PDPSliderContentItem>
	title: Elements.TextElement
	productLabelText: Elements.TextElement
	productLabelUrl: Elements.TextElement
	safetyDataText: Elements.TextElement
	safetyDataUrl: Elements.TextElement
	description: Elements.TextElement
	ratings: Elements.LinkedItemsElement<BazaarVoiceRatingsContentItem>
	skuProvider: Elements.LinkedItemsElement<SkuProviderContentItem>
	pearCommerce: Elements.LinkedItemsElement<PearCommerceWidgetContentItem>
	pdpHeaderAccordion: Elements.LinkedItemsElement<PDPHeaderAccordionContentItem>
	pdpPopupRebates: Elements.LinkedItemsElement<PDPPopupRebatesContentItem>
	pdpPopupRichText: Elements.LinkedItemsElement<PDPPopupWithRichTextContentItem>
	pdpWithFindAVetCard: Elements.LinkedItemsElement<PDPFindAVetCardContentItem>
	buyOnlineCta: Elements.LinkedItemsElement<MikmakButtonContentItem>
	findVetCta: Elements.LinkedItemsElement<CtaButtonContentItem>
}>

const PDPHeaderBlockComponent: Block<PDPHeaderBlockContentItem> = ({
	block,
	BlockMapper,
	globalConfig,
	page,
}) => {
	// Destructure elements with explicit types for clarity
	const {
		slider,
		title,
		productLabelText,
		productLabelUrl,
		safetyDataText,
		safetyDataUrl,
		description,
		ratings,
		skuProvider,
		pearCommerce,
		pdpHeaderAccordion,
		pdpPopupRebates,
		pdpPopupRichText,
		pdpWithFindAVetCard,
		buyOnlineCta,
		findVetCta,
	} = block.elements

	const {colorId} = colorWithFallback({
		pageConfig: page.elements.config[0],
	})

	const brandColorClasses = getBrandColorClasses(colorId)

	const {locale = 'us'} = useRouter()

	const isUsLocale = locale === 'us'

	return (
		<div
			className="pdp-header-container mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-12"
			data-kontent-element-codename="pdp_header_block"
		>
			{/* Desktop Layout: Slider on left, content on right */}
			<div className="flex flex-col gap-6 lg:flex-row lg:gap-12 xl:gap-16">
				{/* Left Column - Sticky Slider */}
				{/* lg:!top-20 = 80px offset to account for fixed header height */}
				<div className="order-1 hidden w-full lg:sticky lg:!top-20 lg:block lg:h-fit lg:w-1/2 lg:!pt-8">
					{/* Slider for desktop */}
					{slider.length > 0 && slider[0] ? (
						<div data-kontent-element-codename="slider">
							<SliderBlock
								BlockMapper={BlockMapper}
								block={slider[0]}
								config={{
									thumbnailPosition: 'left',
									position: 'center',
									enableZoom: true,
									showThumbnails: true,
								}}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}
				</div>

				{/* Right Column */}
				<div className="order-2 flex-1 space-y-6 lg:order-2">
					{/* Title */}
					{title ? (
						<h1
							className="text-gray-900 text-2xl font-bold lg:text-3xl"
							data-kontent-element-codename="title"
						>
							{title}
						</h1>
					) : null}

					{/* Product Buttons */}
					{(productLabelText && productLabelUrl) ||
					(safetyDataText && safetyDataUrl) ? (
						<div
							className="flex flex-col gap-3 sm:flex-row sm:gap-4"
							data-kontent-element-codename="product_label_text"
						>
							<div className="flex flex-wrap gap-3">
								{productLabelText && productLabelUrl ? (
									<TextButton
										className="!text-sm font-light not-italic"
										href={productLabelUrl}
										target="_blank"
										variant="none"
									>
										{productLabelText}
									</TextButton>
								) : null}
								{safetyDataUrl && safetyDataText ? (
									<TextButton
										className="ml-2 !text-sm font-light not-italic"
										href={safetyDataUrl}
										target="_blank"
										variant="none"
									>
										{safetyDataText}
									</TextButton>
								) : null}
							</div>
						</div>
					) : null}

					{/* Description */}
					{description ? (
						<div
							className="text-gray-900 text-base leading-relaxed lg:text-base"
							data-kontent-element-codename="description"
						>
							{description}
						</div>
					) : null}

					{/* Ratings*/}
					{isUsLocale && ratings.length > 0 && ratings[0] ? (
						<div data-kontent-element-codename="ratings">
							<BazaarVoiceRatingsBlock
								BlockMapper={BlockMapper}
								block={ratings[0]}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}

					{/* PDP Popup Components - Rebate Available & Prescription Required buttons */}
					{(isUsLocale && pdpPopupRebates.length > 0) ||
					(isUsLocale && pdpPopupRichText.length > 0) ? (
						<div className="flex flex-wrap items-center gap-3">
							{pdpPopupRebates.length > 0 &&
							pdpPopupRebates[0] ? (
								<div data-kontent-element-codename="pdp_popup_rebates">
									<PDPPopupRebatesBlock
										BlockMapper={BlockMapper}
										block={pdpPopupRebates[0]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							) : null}
							{pdpPopupRichText.length > 0 &&
							pdpPopupRichText[0] ? (
								<div data-kontent-element-codename="pdp_popup_rich_text">
									<PDPPopupWithRichTextBlock
										BlockMapper={BlockMapper}
										block={pdpPopupRichText[0]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							) : null}
						</div>
					) : null}

					{/* Slider (MOBILE ONLY) */}
					{slider.length > 0 && slider[0] ? (
						<div
							className="lg:hidden"
							data-kontent-element-codename="slider"
						>
							<SliderBlock
								BlockMapper={BlockMapper}
								block={slider[0]}
								config={{
									thumbnailPosition: 'left',
									position: 'center',
									enableZoom: true,
									showThumbnails: true,
								}}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}

					{/* SKU Provider */}
					{isUsLocale && skuProvider.length > 0 && skuProvider[0] ? (
						<div data-kontent-element-codename="sku_provider">
							<SkuProviderBlock
								BlockMapper={BlockMapper}
								block={skuProvider[0]}
								globalConfig={globalConfig}
								page={page}
							/>
						</div>
					) : null}

					{isUsLocale ? (
						<div className="flex flex-col gap-[20px]">
							<div>
								{pdpWithFindAVetCard.length > 0 &&
								pdpWithFindAVetCard[0] ? (
									<p className="text-[16px] font-black md:font-medium">
										{
											pdpWithFindAVetCard[0].elements
												.buyOrFindAVetCardTitle
										}
									</p>
								) : null}
							</div>
							<div className="flex flex-col gap-[16px] md:flex-row md:gap-[10px]">
								{pdpWithFindAVetCard.length > 0 &&
								pdpWithFindAVetCard[0] ? (
									<div
										className={`max-w-[328px] basis-4/6 rounded border-2 bg-white p-[16px] ${brandColorClasses.border}`}
										data-kontent-element-codename="pdp_with_find_a_vet_card"
									>
										<PDPFindAVetCard
											BlockMapper={BlockMapper}
											block={pdpWithFindAVetCard[0]}
											globalConfig={globalConfig}
											page={page}
										/>
									</div>
								) : null}
								{/* Pear Commerce Widget */}
								{pearCommerce.length > 0 && pearCommerce[0] ? (
									<div
										className={`max-w-[362px] basis-5/6 rounded border-2 bg-white p-[16px] ${brandColorClasses.border}`}
										data-kontent-element-codename="pear_commerce"
									>
										<PearCommerceWidget
											BlockMapper={BlockMapper}
											block={pearCommerce[0]}
											globalConfig={globalConfig}
											page={page}
										/>
									</div>
								) : null}
							</div>
						</div>
					) : null}

					{!isUsLocale ? (
						<div className="flex w-full flex-col items-center gap-2 md:w-auto md:flex-row">
							{buyOnlineCta[0] ? (
								<div
									className="-mt-2"
									data-kontent-item-id={
										buyOnlineCta[0].system.id
									}
								>
									<MikMakButtonBlock
										BlockMapper={BlockMapper}
										block={buyOnlineCta[0]}
										globalConfig={globalConfig}
										page={page}
									/>
								</div>
							) : null}

							{findVetCta[0] ? (
								<Link
									href={findVetCta[0].elements.url}
									legacyBehavior
									passHref
								>
									<Button
										data-kontent-element-codename="button_text"
										onClick={() => {
											pushToDataLayer({
												event: 'cta_click',
												cta_name:
													findVetCta[0]?.system.id,
												cta_category: 'CTA_Button',
											})
										}}
										variant={
											findVetCta[0].elements.variant[0]
												?.codename ?? 'primary'
										}
									>
										{findVetCta[0].elements.text}
									</Button>
								</Link>
							) : null}
						</div>
					) : null}

					{/* PDP Header Accordion */}
					{pdpHeaderAccordion.length > 0 ? (
						<div
							className="w-full"
							data-kontent-element-codename="pdp_header_accordion"
						>
							{pdpHeaderAccordion.map((accordion) => (
								<PDPHeaderAccordionBlock
									BlockMapper={BlockMapper}
									block={accordion}
									globalConfig={globalConfig}
									key={accordion.system.id}
									page={page}
								/>
							))}
						</div>
					) : null}
				</div>
			</div>
		</div>
	)
}

export const PDPHeaderBlock = memo(PDPHeaderBlockComponent)
